import { supabase } from '../../../lib/supabase';
import { Lead } from '../../../domain/entities/Lead';
import { InfrastructureError } from '../../../core/errors/DomainErrors';
import { LeadMapper } from '../mappers/LeadMapper';
import { LeadValidator } from '../validators/LeadValidator';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[LeadPersistence]');

/**
 * LeadPersistence
 *
 * Camada de persistência para leads/oportunidades.
 * - `save` retorna o `id` real do banco em criações (string) ou `undefined` em updates.
 * - Erros do Supabase NUNCA são silenciados: sempre lançam `InfrastructureError`.
 * - Logs sem PII (apenas chaves e tipos).
 */
export class LeadPersistence {
    private static readonly TABLE_NAME = 'opportunities';

    static async save(lead: Lead, organizationId?: string): Promise<string | undefined> {
        try {
            const isNew = LeadValidator.isTemporaryId(lead.id);
            const dbLead = LeadMapper.toDatabase(lead, isNew);

            log.debug('save', {
                isNew,
                hasTitle: !!dbLead.title,
                hasStage: !!dbLead.stage,
                status: dbLead.status,
                hasOrigin: !!dbLead.origin,
                hasContactId: !!dbLead.contact_id
            });

            LeadValidator.validateRequiredFields(dbLead);

            let createdId: string | undefined;
            if (isNew) {
                LeadValidator.validateOrganizationId(organizationId);
                createdId = await this.create(dbLead, organizationId!);
            } else {
                await this.update(lead.id, dbLead);
            }

            return createdId;
        } catch (error) {
            if (error instanceof InfrastructureError) throw error;
            log.error('save unexpected error', { name: (error as Error)?.name });
            throw new InfrastructureError('Erro inesperado ao salvar negociação');
        }
    }

    static async delete(id: string): Promise<void> {
        try {
            LeadValidator.validateId(id, 'ID da oportunidade');

            // 1. Delete associated tasks first (FK constraint)
            const { error: tasksError } = await supabase
                .from('tasks')
                .delete()
                .eq('opportunity_id', id);

            if (tasksError) {
                log.error('delete tasks error', { code: tasksError.code });
                throw new InfrastructureError(`Erro ao limpar tarefas da oportunidade: ${tasksError.message}`);
            }

            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .delete()
                .eq('id', id)
                .select();

            if (error) {
                log.error('delete error', { code: error.code });
                throw new InfrastructureError(`Erro ao deletar negociação: ${error.message}`, error);
            }

            if (!data || data.length === 0) {
                log.warn('delete no-op (already gone)');
                return;
            }
        } catch (error) {
            if (error instanceof InfrastructureError) throw error;
            log.error('delete unexpected error', { name: (error as Error)?.name });
            throw new InfrastructureError('Erro inesperado ao deletar negociação');
        }
    }

    private static async create(dbLead: any, organizationId: string): Promise<string> {
        const { id: _ignored, ...leadWithoutId } = dbLead;

        const leadToInsert = {
            ...leadWithoutId,
            organization_id: organizationId
        };

        const { data, error } = await supabase
            .from(this.TABLE_NAME)
            .insert([leadToInsert])
            .select('id')
            .single();

        if (error) {
            log.error('create error', { code: error.code });
            throw new InfrastructureError(this.translateError(error, 'criar'), error);
        }

        if (!data?.id) {
            throw new InfrastructureError('Negociação criada mas o banco não retornou o ID');
        }

        return data.id as string;
    }

    private static async update(id: string, dbLead: any): Promise<void> {
        const { id: _, ...updateData } = dbLead;

        const { error } = await supabase
            .from(this.TABLE_NAME)
            .update(updateData)
            .eq('id', id);

        if (error) {
            log.error('update error', { code: error.code });
            throw new InfrastructureError(this.translateError(error, 'atualizar'), error);
        }
    }

    /**
     * Traduz erros conhecidos do Postgres em mensagens legíveis ao usuário.
     * Códigos: https://www.postgresql.org/docs/current/errcodes-appendix.html
     */
    private static translateError(error: { code?: string; message?: string }, action: 'criar' | 'atualizar'): string {
        switch (error.code) {
            case '23505':
                return `Já existe uma negociação igual para este contato. Confira antes de ${action} novamente.`;
            case '23503':
                return `Referência inválida (contato, etapa do funil ou usuário responsável não existe na sua organização).`;
            case '23502':
                return `Há campos obrigatórios faltando para ${action} a negociação.`;
            case '23514':
                return `Algum valor está fora do permitido (ex.: status inválido ou valor negativo).`;
            case '42501':
                return 'Você não tem permissão para essa ação nesta organização.';
            case 'PGRST116':
                return 'Negociação não encontrada (pode ter sido removida por outro usuário).';
            default:
                return `Erro ao ${action} negociação: ${error.message || 'erro desconhecido'}`;
        }
    }
}
